import { useEffect, useRef } from "react";
import {
  queueMobileScrollSnap,
  setJourneyMobileScrollGate,
} from "@/journeyScrollGate";
import { MOBILE_ANCHOR_NAV_EVENT } from "@/mobileAnchorNav";
import { mobileStoryHashesBelowStory } from "@/mobileStoryNav";
import { getAssetUrl } from "@/lib/assetBaseUrl";

const MOBILE_LIVE_BELOW_HASHES = mobileStoryHashesBelowStory("#live");
const MOBILE_LIVE_FINAL_STEP = 3;

const CARD_IMAGES = {
  c1: "https://asset.sparkl.me/pb/poc-folder/CkEZa2Z0eTf2koZn7XQGFBA39xWRlPqzc8VCOR6a.png",
  c2: "https://asset.sparkl.me/pb/poc-folder/6lp8EJYzZubloXvZyoHAHtZuHUeFb8dqOmyAgWwx.png",
  c3: "https://asset.sparkl.me/pb/poc-folder/i3JVvJa1eKDLxjysiLESYs8c1BcctXcWlXF2YuDz.png",
  c4: "pb/assets/img/fMmbvTBha7pftaFWGVnUdVFpEVcX6Fvn0BJvbePC.png",
};

const CARDS = [
  {
    id: "live_card_01",
    eyebrow: "Structured live classes",
    title: "Personalized learning path in live class",
    body:
      "Every live class is structured with concept teaching, quizzes, interactive activities, and guided practice — so students actively learn instead of passively attending.",
    image: CARD_IMAGES.c1,
    alt: "Personalized learning path in a live class",
    rest: { x: 0, y: 0, rot: -1.8, scale: 1 },
    exit: { x: -110, y: -18, rot: -12 }, // vw / vh / deg
  },
  {
    id: "live_card_02",
    eyebrow: "NoteVision",
    title: "Teacher sees exactly what the student is writing",
    body:
      "With our innovative NoteVision camera, teachers can see everything a student writes in real time — just as if they were sitting right beside them.",
    image: CARD_IMAGES.c2,
    alt: "NoteVision camera showing student written work",
    rest: { x: 20, y: 22, rot: 2.2, scale: 0.985 },
    exit: { x: -112, y: 18, rot: 11 },
  },
  {
    id: "live_card_03",
    eyebrow: "Assessment",
    title: "Tests with invigilation",
    body:
      "Periodic, invigilated assessments ensure an accurate understanding of student performance — enabling a more tailored and effective course plan.",
    image: CARD_IMAGES.c3,
    alt: "Invigilated online test",
    rest: { x: -18, y: 42, rot: -2.4, scale: 0.97 },
    exit: { x: -116, y: -7, rot: -9 },
  },
  {
    id: "live_card_04",
    eyebrow: "Teacher support",
    title: "Doubt resolution with teachers",
    body:
      "Connect with your teachers over chat anytime to clear your doubts quickly and keep learning moving between classes.",
    imagePath: CARD_IMAGES.c4,
    alt: "Teacher doubt resolution over chat",
    rest: { x: 28, y: 62, rot: 2.8, scale: 0.955 },
    exit: { x: -105, y: 12, rot: 8 },
  },
];

// Beats in normalised section scroll [0, 1].
// Designed so 1 card cycle (hold + transition) ≈ 100vh of scroll.
// Heights in CSS: height = 100 × _T / cycle_units  (see index.css comments).
//   entry:              0.15 units (desktop HEAD) / 0.10 units (mobile tuned)
//   card hold (×3):     0.25 units each  ← minimal pause
//   card transition(×3):0.65 units each  ← fast swipe
//   card 4 final hold:  0.30 units
//   _T = 0.10 + 3×(0.25+0.65) + 0.30 = 3.10
//   cycle = 0.90 units → at 360vh desktop: 0.90/3.10×360 ≈ 104vh per card ✓
const _T = 3.1;
const ENTRY_END        = 0.15 / _T;  // desktop (HEAD value)
const C1_HOLD_END = 0.35 / _T;
const C1_EXIT_END = 1.00 / _T;
const C2_HOLD_END = 1.25 / _T;
const C2_EXIT_END = 1.90 / _T;
const C3_HOLD_END = 2.15 / _T;
const C3_EXIT_END = 2.80 / _T;

const ACTIVE = { x: 0, y: 0, rot: 0, scale: 1 };

/** Matches {@link App.js} mobile Lenis breakpoint. */
const LIVE_MOBILE_MQ = "(max-width: 767px)";

/** Desktop: HEAD value — unchanged. */
const LIVE_SCROLL_SMOOTH_DESKTOP = 0.14;

/** Mobile — discrete card steps (one swipe = one full card transition). */
const MOBILE_LIVE_BAND_TOP = [0, 0.25, 0.5, 0.75, 1];
const MOBILE_LIVE_BAND_EPS = 0.004;
/** After pin / section load — one short swipe crosses this (≈17vh on 403vh section). */
const MOBILE_LIVE_FIRST_ADVANCE_P = 0.07;
const MOBILE_LIVE_ANIM_EPS = 0.008;
/** Normalised p per second — ~0.28s per card transition. */
const MOBILE_LIVE_ANIM_SPEED = 0.82;
const MOBILE_LIVE_LEAVE_TOP_PX = 80;
/** Finger travel (px) on card 1 to arm upward section exit. */
const MOBILE_LIVE_EXIT_UP_SWIPE_PX = 24;
/** Section top drift (px) before pin correction snap fires. */
const MOBILE_LIVE_PIN_CORRECT_PX = 6;
const MOBILE_LIVE_SCROLL_GATE_OWNER = "live";
/** Settled visual p per card step — step 0 at rest (p=0) so first swipe advances immediately. */
const MOBILE_LIVE_STEP_TARGETS = [0, C1_EXIT_END, C2_EXIT_END, C3_EXIT_END];

/** Mobile rest: keep stacked offset + rotation, no scale. */
function mobileRest(card) {
  return { x: card.rest.x, y: card.rest.y, rot: card.rest.rot, scale: 1 };
}

function mobileLiveScrollStepFromP(p) {
  if (p < MOBILE_LIVE_BAND_TOP[1]) return 0;
  if (p < MOBILE_LIVE_BAND_TOP[2]) return 1;
  if (p < MOBILE_LIVE_BAND_TOP[3]) return 2;
  return 3;
}

function mobileLiveMaxScrollP(displayStep, animating) {
  if (displayStep < 0) {
    return MOBILE_LIVE_BAND_TOP[1] - MOBILE_LIVE_BAND_EPS;
  }
  if (displayStep >= 3) {
    return 1 - MOBILE_LIVE_BAND_EPS;
  }
  const maxBandIdx = animating ? displayStep + 1 : displayStep + 2;
  return MOBILE_LIVE_BAND_TOP[maxBandIdx] - MOBILE_LIVE_BAND_EPS;
}

function mobileLiveMinScrollP(displayStep, animating, allowExitUp) {
  if (displayStep <= 0) {
    return allowExitUp ? null : 0;
  }
  if (displayStep >= 3) {
    return MOBILE_LIVE_BAND_TOP[3] + MOBILE_LIVE_BAND_EPS;
  }
  if (animating) {
    return MOBILE_LIVE_BAND_TOP[displayStep] + MOBILE_LIVE_BAND_EPS;
  }
  return MOBILE_LIVE_BAND_TOP[displayStep - 1] + MOBILE_LIVE_BAND_EPS;
}

function mobileLiveSnapScrollY(sectionEl, displayStep, scrollRange) {
  const pinStartY = sectionEl.offsetTop;
  const bandP =
    displayStep <= 0
      ? MOBILE_LIVE_BAND_EPS
      : MOBILE_LIVE_BAND_TOP[displayStep] + MOBILE_LIVE_BAND_EPS;
  return pinStartY + bandP * scrollRange;
}

/**
 * True when scroll crossed exactly one band ahead/behind the committed step.
 * Step 0 forward uses a lower p threshold so the first swipe after pin is not eaten by snap.
 */
function mobileLiveShouldAdvanceStep(displayStep, scrollStep, idle, targetP) {
  if (!idle || displayStep < 0 || scrollStep < 0) return 0;
  if (displayStep < 3) {
    const canForward =
      scrollStep === displayStep + 1 ||
      (displayStep === 0 && targetP >= MOBILE_LIVE_FIRST_ADVANCE_P);
    if (canForward) return 1;
  }
  if (displayStep > 0) {
    const canReverse =
      scrollStep === displayStep - 1 ||
      (displayStep === 1 && targetP < MOBILE_LIVE_FIRST_ADVANCE_P);
    if (canReverse) return -1;
  }
  return 0;
}

function liveStepTargetP(step) {
  return MOBILE_LIVE_STEP_TARGETS[
    Math.min(Math.max(step, 0), MOBILE_LIVE_STEP_TARGETS.length - 1)
  ];
}
function clamp01(v) {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** Desktop card easing — HEAD smoothstep (unchanged from committed baseline). */
function ease(t) {
  const x = clamp01(t);
  return x * x * (3 - 2 * x);
}
/** Mobile card easing — stronger S-curve for more deliberate swipe feel. */
function easeInOutCubic(t) {
  const x = clamp01(t);
  return x < 0.5 ? 4 * x * x * x : 1 - (-2 * x + 2) ** 3 / 2;
}
function lerp(a, b, t) {
  return a + (b - a) * t;
}
function lerpState(a, b, t) {
  return {
    x: lerp(a.x, b.x, t),
    y: lerp(a.y, b.y, t),
    rot: lerp(a.rot, b.rot, t),
    scale: lerp(a.scale ?? 1, b.scale ?? 1, t),
  };
}

/**
 * Build the 4 card states for a given normalised scroll p in [0,1].
 * easeFn: ease (desktop/HEAD) or easeInOutCubic (mobile-tuned).
 */
function computeCardStates(p, easeFn, mobileMode = false) {
  const rest = (card) => (mobileMode ? mobileRest(card) : card.rest);
  let s1 = { ...rest(CARDS[0]) };
  let s2 = { ...rest(CARDS[1]) };
  let s3 = { ...rest(CARDS[2]) };
  let s4 = { ...rest(CARDS[3]) };

  // Beat 1: card 1 exits, card 2 rises
  if (p >= C1_HOLD_END) {
    const t = easeFn(
      clamp01((p - C1_HOLD_END) / (C1_EXIT_END - C1_HOLD_END))
    );
    s1 = lerpState(rest(CARDS[0]), { ...CARDS[0].exit, scale: 1 }, t);
    s2 = lerpState(rest(CARDS[1]), ACTIVE, t);
  }
  // Beat 2: card 2 exits, card 3 rises
  if (p >= C2_HOLD_END) {
    const t = easeFn(
      clamp01((p - C2_HOLD_END) / (C2_EXIT_END - C2_HOLD_END))
    );
    s2 = lerpState(ACTIVE, { ...CARDS[1].exit, scale: 1 }, t);
    s3 = lerpState(rest(CARDS[2]), ACTIVE, t);
  }
  // Beat 3: card 3 exits, card 4 rises
  if (p >= C3_HOLD_END) {
    const t = easeFn(
      clamp01((p - C3_HOLD_END) / (C3_EXIT_END - C3_HOLD_END))
    );
    s3 = lerpState(ACTIVE, { ...CARDS[2].exit, scale: 1 }, t);
    s4 = lerpState(rest(CARDS[3]), ACTIVE, t);
  }
  return [s1, s2, s3, s4];
}

/**
 * Convert a state (x in vw, y in vh OR y in px when card is in rest, etc.)
 * to a CSS transform. Cards' REST offsets are in pixels, while EXIT
 * offsets are in viewport units. We treat the units consistently by
 * marking exit fields with vw/vh suffixes.
 */
function stateToTransform(state, useVwVh) {
  const xUnit = useVwVh ? "vw" : "px";
  const yUnit = useVwVh ? "vh" : "px";
  return `translate3d(${state.x}${xUnit}, ${state.y}${yUnit}, 0) rotate(${state.rot}deg) scale(${state.scale})`;
}

/**
 * Determine whether a card is currently in/transitioning to its EXIT path.
 * Exit positions are in viewport units (vw/vh); rest positions are in px.
 * We pick which unit to use based on whether the card is past its hold-end
 * threshold. While transitioning the card is still numerically between
 * rest (px) and exit (vw/vh) — to keep things simple we switch units at
 * the hold-end boundary and accept a tiny visual jump (which is hidden by
 * the surrounding card stack at that moment).
 */
function cardUsesViewportUnits(idx, p) {
  if (idx === 0) return p >= C1_HOLD_END;
  if (idx === 1) return p >= C2_HOLD_END;
  if (idx === 2) return p >= C3_HOLD_END;
  return false;
}

export default function SparklLiveSection() {
  const sectionRef = useRef(null);
  const innerRef = useRef(null);
  const stackRef = useRef(null);
  const titleRef = useRef(null);
  const cardRefs = useRef([]);
  const mobLiveStepRef = useRef(-1);
  const mobLiveVisualPRef = useRef(0);
  const mobLiveExitArmedRef = useRef(false);
  const mobLiveTouchAtStep0Ref = useRef(false);
  const mobLiveEnteredFromBelowRef = useRef(false);
  const lastTimeRef = useRef(performance.now());

  useEffect(() => {
    const onAnchorNav = (e) => {
      const hash = e.detail?.hash;
      if (hash === "#live") {
        mobLiveEnteredFromBelowRef.current = false;
        mobLiveStepRef.current = -1;
        mobLiveVisualPRef.current = liveStepTargetP(0);
        mobLiveExitArmedRef.current = false;
        mobLiveTouchAtStep0Ref.current = false;
        return;
      }
      if (hash && MOBILE_LIVE_BELOW_HASHES.has(hash)) {
        mobLiveEnteredFromBelowRef.current = true;
        mobLiveStepRef.current = -1;
        mobLiveVisualPRef.current = 0;
        mobLiveExitArmedRef.current = false;
        mobLiveTouchAtStep0Ref.current = false;
      }
    };
    window.addEventListener(MOBILE_ANCHOR_NAV_EVENT, onAnchorNav);
    return () => window.removeEventListener(MOBILE_ANCHOR_NAV_EVENT, onAnchorNav);
  }, []);

  useEffect(() => {
    const sec = sectionRef.current;
    const stack = stackRef.current;
    const title = titleRef.current;
    if (!sec || !stack) return undefined;

    const mq = window.matchMedia(LIVE_MOBILE_MQ);
    const mobileRef = { current: mq.matches };

    // On mobile the head title AND card stack fade in via CSS + IntersectionObserver
    // (same pattern as SparklCoursesSection). On desktop the JS RAF loop
    // drives the opacity so the title can also fade out near section end.
    let headIo = null;
    let stackIo = null;
    const setupMobileObservers = (isMobile) => {
      if (isMobile) {
        if (title) {
          title.style.opacity = "";
          title.style.transform = "";
          title.classList.add("is-visible");
        }
        stack.style.opacity = "";
        stack.classList.add("is-visible");
        if (headIo) { headIo.disconnect(); headIo = null; }
        if (stackIo) { stackIo.disconnect(); stackIo = null; }
      } else {
        // Switching back to desktop — remove classes and let JS control opacity.
        if (title) title.classList.remove("is-visible");
        stack.classList.remove("is-visible");
        if (headIo) { headIo.disconnect(); headIo = null; }
        if (stackIo) { stackIo.disconnect(); stackIo = null; }
      }
    };

    setupMobileObservers(mq.matches);

    const onMq = () => {
      mobileRef.current = mq.matches;
      setupMobileObservers(mq.matches);
    };
    mq.addEventListener("change", onMq);

    let smoothP = 0;
    let rafId = 0;
    let touchStartY = 0;

    const isSettledAtCard1 = () =>
      mobLiveStepRef.current === 0 &&
      Math.abs(mobLiveVisualPRef.current - liveStepTargetP(0)) <
        MOBILE_LIVE_ANIM_EPS;

    const onTouchStart = (e) => {
      if (!mobileRef.current) return;
      touchStartY = e.touches[0]?.clientY ?? 0;
      mobLiveTouchAtStep0Ref.current = isSettledAtCard1();
    };

    const tryArmExitUp = (fingerY) => {
      if (!mobileRef.current || !mobLiveTouchAtStep0Ref.current) return;
      if (mobLiveExitArmedRef.current || !isSettledAtCard1()) return;
      if (fingerY - touchStartY >= MOBILE_LIVE_EXIT_UP_SWIPE_PX) {
        mobLiveExitArmedRef.current = true;
      }
    };

    const onTouchMove = (e) => {
      tryArmExitUp(e.touches[0]?.clientY ?? touchStartY);
    };

    const onTouchEnd = (e) => {
      tryArmExitUp(e.changedTouches[0]?.clientY ?? touchStartY);
    };

    sec.addEventListener("touchstart", onTouchStart, { passive: true });
    sec.addEventListener("touchmove", onTouchMove, { passive: true });
    sec.addEventListener("touchend", onTouchEnd, { passive: true });

    const loop = () => {
      rafId = requestAnimationFrame(loop);

      const rect = sec.getBoundingClientRect();
      const total = sec.offsetHeight - window.innerHeight;
      const targetP = clamp01(-rect.top / Math.max(total, 1));
      const isMobile = mobileRef.current;

      const now = performance.now();
      const dt = Math.min(0.1, (now - lastTimeRef.current) / 1000);
      lastTimeRef.current = now;

      let p;

      if (isMobile) {
        const liveInView =
          rect.bottom > 0 && rect.top < window.innerHeight;
        const leftUpward = rect.top > MOBILE_LIVE_LEAVE_TOP_PX;
        let displayStep = mobLiveStepRef.current;
        let visualP = mobLiveVisualPRef.current;
        const prevDisplayStep = displayStep;
        let queueFinaleEntrySnap = false;

        let scrollStep = -1;
        if (liveInView && (rect.top <= 0 || displayStep >= 0)) {
          scrollStep = mobileLiveScrollStepFromP(targetP);
        }

        const idleAtCurrentStep =
          displayStep >= 0 &&
          Math.abs(visualP - liveStepTargetP(displayStep)) <
            MOBILE_LIVE_ANIM_EPS;
        const mayResetUpward =
          leftUpward &&
          (displayStep < 0 ||
            (displayStep === 0 &&
              idleAtCurrentStep &&
              mobLiveExitArmedRef.current));
        const mayResetPast =
          rect.bottom <= 0 &&
          (displayStep < 0 || idleAtCurrentStep);

        let didReset = false;
        if (mayResetUpward) {
          mobLiveEnteredFromBelowRef.current = false;
          displayStep = -1;
          visualP = 0;
          mobLiveExitArmedRef.current = false;
          didReset = true;
        } else if (mayResetPast) {
          mobLiveEnteredFromBelowRef.current = true;
          displayStep = -1;
          visualP = 0;
          mobLiveExitArmedRef.current = false;
          didReset = true;
        } else if (displayStep !== 0) {
          mobLiveExitArmedRef.current = false;
        }

        if (!didReset && displayStep < 0 && rect.top <= 0) {
          if (mobLiveEnteredFromBelowRef.current) {
            displayStep = MOBILE_LIVE_FINAL_STEP;
            visualP = liveStepTargetP(MOBILE_LIVE_FINAL_STEP);
            mobLiveEnteredFromBelowRef.current = false;
            queueFinaleEntrySnap = true;
          } else {
            const entryStep = mobileLiveScrollStepFromP(targetP);
            displayStep = entryStep;
            visualP = liveStepTargetP(entryStep);
          }
        }

        if (!didReset && dt > 0 && displayStep >= 0) {
          const targetVisual = liveStepTargetP(displayStep);
          const idle =
            Math.abs(visualP - targetVisual) < MOBILE_LIVE_ANIM_EPS;
          const advanceDir = mobileLiveShouldAdvanceStep(
            displayStep,
            scrollStep,
            idle,
            targetP,
          );
          if (advanceDir !== 0 && !queueFinaleEntrySnap) {
            displayStep += advanceDir;
          }

          const goalP = liveStepTargetP(displayStep);
          const diff = goalP - visualP;
          const step = MOBILE_LIVE_ANIM_SPEED * dt;
          if (Math.abs(diff) <= step) {
            visualP = goalP;
          } else {
            visualP += Math.sign(diff) * step;
          }
        }

        mobLiveStepRef.current = displayStep;
        mobLiveVisualPRef.current = visualP;
        p =
          displayStep < 0 && liveInView
            ? liveStepTargetP(mobileLiveScrollStepFromP(targetP))
            : visualP;

        const animating =
          displayStep >= 0 &&
          Math.abs(visualP - liveStepTargetP(displayStep)) >=
            MOBILE_LIVE_ANIM_EPS;
        const settledAtStep = displayStep >= 0 && !animating;
        const allowExitUp =
          settledAtStep &&
          displayStep === 0 &&
          mobLiveExitArmedRef.current;
        const allowUpwardLeave = allowExitUp;
        const allowForwardExit = displayStep >= 3 && settledAtStep;
        const keepPinLocked =
          liveInView &&
          displayStep >= 0 &&
          !allowForwardExit &&
          !allowUpwardLeave;

        if (keepPinLocked) {
          const maxP = mobileLiveMaxScrollP(displayStep, animating);
          const minP = mobileLiveMinScrollP(displayStep, animating, false);
          const pinStartY = sec.offsetTop;
          const sectionEndY = pinStartY + total;
          const minScrollY =
            minP != null ? pinStartY + minP * total : pinStartY;
          const maxScrollY = Math.min(
            pinStartY + maxP * total,
            sectionEndY,
          );
          setJourneyMobileScrollGate(
            true,
            minScrollY,
            maxScrollY,
            pinStartY,
            sectionEndY,
            MOBILE_LIVE_SCROLL_GATE_OWNER,
          );

          if (rect.top > MOBILE_LIVE_PIN_CORRECT_PX) {
            queueMobileScrollSnap(
              mobileLiveSnapScrollY(sec, displayStep, total),
              MOBILE_LIVE_SCROLL_GATE_OWNER,
            );
          }

          const skipPinSnap = prevDisplayStep === -1 && displayStep === 0;
          if (queueFinaleEntrySnap) {
            queueMobileScrollSnap(
              mobileLiveSnapScrollY(sec, MOBILE_LIVE_FINAL_STEP, total),
              MOBILE_LIVE_SCROLL_GATE_OWNER,
            );
          } else if (
            displayStep !== prevDisplayStep &&
            displayStep >= 0 &&
            !skipPinSnap
          ) {
            queueMobileScrollSnap(
              mobileLiveSnapScrollY(sec, displayStep, total),
              MOBILE_LIVE_SCROLL_GATE_OWNER,
            );
          } else if (
            settledAtStep &&
            scrollStep > displayStep + 1 &&
            displayStep < 3
          ) {
            queueMobileScrollSnap(
              mobileLiveSnapScrollY(sec, displayStep + 1, total),
              MOBILE_LIVE_SCROLL_GATE_OWNER,
            );
          } else if (
            settledAtStep &&
            scrollStep < displayStep - 1 &&
            displayStep > 0
          ) {
            queueMobileScrollSnap(
              mobileLiveSnapScrollY(sec, displayStep - 1, total),
              MOBILE_LIVE_SCROLL_GATE_OWNER,
            );
          }
        } else {
          setJourneyMobileScrollGate(
            false,
            undefined,
            undefined,
            undefined,
            undefined,
            MOBILE_LIVE_SCROLL_GATE_OWNER,
          );
        }
      } else {
        smoothP += (targetP - smoothP) * LIVE_SCROLL_SMOOTH_DESKTOP;
        p = smoothP;
      }

      if (rect.top > window.innerHeight + 50 || rect.bottom < -50) {
        if (isMobile) {
          setJourneyMobileScrollGate(
            false,
            undefined,
            undefined,
            undefined,
            undefined,
            MOBILE_LIVE_SCROLL_GATE_OWNER,
          );
        }
        return;
      }

      if (isMobile) {
        stack.style.removeProperty("transform");
      } else {
        const entryLin = clamp01(p / ENTRY_END);
        const entry = clamp01(entryLin);
        const stackScale = lerp(0.96, 1, entry);
        const stackY = lerp(20, 0, entry);
        stack.style.transform = `translate3d(0, ${stackY}px, 0) scale(${stackScale})`;
        stack.style.opacity = String(entry);
      }

      if (title && !isMobile) {
        const titleIn = clamp01(p / 0.02);
        const titleOut = clamp01((p - 0.94) / 0.06);
        const titleY = lerp(18, 0, titleIn) - titleOut * 18;
        title.style.opacity = String(titleIn * (1 - titleOut));
        title.style.transform = `translate3d(0, ${titleY}px, 0)`;
      }

      const cardEase = isMobile ? easeInOutCubic : ease;
      const states = computeCardStates(p, cardEase, isMobile);
      states.forEach((state, i) => {
        const el = cardRefs.current[i];
        if (!el) return;
        const useVwVh = cardUsesViewportUnits(i, p);
        el.style.transform = stateToTransform(state, useVwVh);
      });
    };

    rafId = requestAnimationFrame(loop);

    return () => {
      sec.removeEventListener("touchstart", onTouchStart);
      sec.removeEventListener("touchmove", onTouchMove);
      sec.removeEventListener("touchend", onTouchEnd);
      mq.removeEventListener("change", onMq);
      if (headIo) headIo.disconnect();
      if (stackIo) stackIo.disconnect();
      setJourneyMobileScrollGate(
        false,
        undefined,
        undefined,
        undefined,
        undefined,
        MOBILE_LIVE_SCROLL_GATE_OWNER,
      );
      cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      id="live"
      className="sp-live"
      data-sp-snap-section
      data-testid="live-section"
    >
      <div ref={innerRef} className="sp-live-inner">
        <div ref={titleRef} className="sp-live-head" data-testid="live-head">
          <h2 className="sp-live-title">
          1-on-1 Tutoring, built around how{" "}
            <span className="sp-grad-word">students really learn.</span>
          </h2>
          <p className="sp-live-sub">
            From structured live classes to NoteVision, invigilated tests,
            and teacher-led doubt resolution — 1-on-1 Tutoring gives every
            student focused academic support.
          </p>
        </div>

        <div className="sp-live-stage" data-testid="live-stage">
          <div ref={stackRef} className="sp-live-stack" data-testid="live-stack">
            {CARDS.map((c, i) => (
              <article
                key={c.id}
                ref={(el) => (cardRefs.current[i] = el)}
                className="sp-live-card"
                data-testid={c.id}
                style={{ zIndex: 40 - i * 10 }}
              >
                <div className="sp-live-card-img-wrap">
                  <img
                    src={c.imagePath ? getAssetUrl(c.imagePath) : c.image}
                    alt={c.alt}
                    className="sp-live-card-img"
                    loading="lazy"
                  />
                </div>
                <div className="sp-live-card-text">
                  <span className="sp-live-card-eyebrow">{c.eyebrow}</span>
                  <h3 className="sp-live-card-title">{c.title}</h3>
                  <p className="sp-live-card-body">{c.body}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
